import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft } from "lucide-react";

const NAVY = "#0b192c", MUTED = "#475569";

function formatErr(d) {
  if (d == null) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email");
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
        { email: email.trim().toLowerCase() },
        { withCredentials: true }
      );
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Could not send reset email");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#fdfcf8" }}>
      <div className="w-full max-w-md p-8" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold" style={{ color: MUTED }} data-testid="forgot-back-link">
          <ArrowLeft size={12} /> Back to sign in
        </Link>
        <div className="text-[11px] uppercase tracking-[0.2em] font-semibold mt-8" style={{ color: MUTED }}>Password reset</div>
        <h1 className="font-serif text-4xl mt-2 leading-none" style={{ color: NAVY }}>Forgot your password?</h1>
        <p className="text-sm mt-3" style={{ color: MUTED }}>
          Enter the email tied to your AIFLedger account and we&apos;ll send you a secure link
          to choose a new password.
        </p>

        {sent ? (
          <div className="mt-8 p-5 border rounded-lg" style={{ background: "#f5faf5", borderColor: "#c7e8c9" }}
               data-testid="forgot-sent">
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#166534" }}>
              <Mail size={16} /> Check your inbox
            </div>
            <p className="text-sm mt-2" style={{ color: MUTED }}>
              If <b>{email}</b> matches an active account, you&apos;ll receive a reset link within a minute.
              The link is valid for 30 minutes and can only be used once.
            </p>
            <p className="text-xs mt-4" style={{ color: MUTED }}>
              Don&apos;t see it? Check your spam folder or contact{" "}
              <a className="font-semibold" style={{ color: NAVY }} href="mailto:reach@aifledger.com">reach@aifledger.com</a>.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Email</Label>
              <Input id="email" type="email" required value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     className="mt-1.5 bg-white border-[#e2e1dd]"
                     data-testid="forgot-email-input" />
            </div>
            <Button type="submit" disabled={loading}
                    className="w-full bg-[#0b192c] hover:bg-[#0b192c]/90 text-white font-semibold h-11"
                    data-testid="forgot-submit-btn">
              {loading ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
